import React, { useEffect, useState } from "react";
import {
  Trash2,
  UserPlus,
  ShieldCheck,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Settings,
  Tag,
  Save,
  Pencil,
} from "lucide-react";
import {
  IconButton,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import APIService from "../../Api/ApiService";
import { UserProfile } from "../../Models/Types";
import "./AdminPage.scss";

// Type for Tag Configs updated with pause_minutes
interface TagConfig {
  tag_name: string;
  threshold: number;
  pause_minutes?: number;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Users State ---
  const [newUserEmail, setNewUserEmail] = useState("");
  const [roleChangeUser, setRoleChangeUser] = useState<UserProfile | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // --- User Delete Modal State ---
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [isUserDeleteDialogOpen, setIsUserDeleteDialogOpen] = useState(false);

  // --- Settings State ---
  const [globalThreshold, setGlobalThreshold] = useState<number>(10);
  const [pauseMinutes, setPauseMinutes] = useState<number>(10);
  const [groupPauseMinutes, setGroupPauseMinutes] = useState<number>(10); // NEW: Group pause state
  const [tags, setTags] = useState<TagConfig[]>([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagThreshold, setNewTagThreshold] = useState<number>(10);
  const [newTagPause, setNewTagPause] = useState<number>(10); // NEW: Tag specific pause state
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // --- Tag Delete Modal State ---
  const [tagToDelete, setTagToDelete] = useState<TagConfig | null>(null);
  const [isTagDeleteDialogOpen, setIsTagDeleteDialogOpen] = useState(false);

  const loadTags = async () => {
    try {
      const status: any = await APIService.GetMonitoredServicesStatus();

      var tagsList: string[] = [];
      for (var i = 0; i < status.length; i++) {
        var tags = status[i].application_tags;
        if (tags) {
          for (var j = 0; j < tags.length; j++) {
            if (tagsList.indexOf(tags[j]) === -1) {
              tagsList.push(tags[j]);
            }
          }
        }
      }

      setAvailableTags(tagsList.sort());
    } catch (err) {
      console.error("Failed to extract tags", err);
    }
  };

  const fetchAdminData = async () => {
    try {
      console.log("Admin: Fetching data (users, tags, settings)...");

      const [usersData, tagsData, globalThreshData, pauseData, groupPauseData] =
        await Promise.all([
          APIService.AdminGetAllUsers(),
          APIService.AdminGetTags(),
          APIService.AdminGetGlobalThreshold(),
          APIService.AdminGetNotificationPause(),
          APIService.AdminGetGroupNotificationPause(), // Ensure this is exported in ApiService
        ]);

      setUsers(usersData);
      setTags(tagsData);
      setGlobalThreshold(globalThreshData.threshold);
      setPauseMinutes(pauseData.pauseMinutes);
      setGroupPauseMinutes(groupPauseData.pauseMinutes);
    } catch (err: any) {
      console.error("Admin: Failed to fetch admin data", err.message);
      alert(
        err.message ||
          "Errore nel caricamento dei dati. Sei un amministratore?",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
    fetchAdminData();
  }, []);

  // --- Settings & Tags Handlers ---

  const handleUpdatePauseMinutes = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log(
        `Admin: Updating notification pause to ${pauseMinutes} minutes`,
      );
      await APIService.AdminUpdateNotificationPause(pauseMinutes);
      setSuccessMessage(
        `Il timeout di pausa notifiche globale è stato aggiornato a ${pauseMinutes} minuti.`,
      );
      setIsSuccessDialogOpen(true);
    } catch (err: any) {
      console.error("Admin: Failed to update notification pause", err.message);
      alert(
        "Errore durante l'aggiornamento della pausa notifiche: " + err.message,
      );
    }
  };

  const handleUpdateGroupPauseMinutes = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log(
        `Admin: Updating group notification pause to ${groupPauseMinutes} minutes`,
      );
      await APIService.AdminUpdateGroupNotificationPause(groupPauseMinutes);
      setSuccessMessage(
        `Il timeout di pausa notifiche per i gruppi è stato aggiornato a ${groupPauseMinutes} minuti.`,
      );
      setIsSuccessDialogOpen(true);
    } catch (err: any) {
      console.error(
        "Admin: Failed to update group notification pause",
        err.message,
      );
      alert(
        "Errore durante l'aggiornamento della pausa gruppi: " + err.message,
      );
    }
  };

  const handleUpdateGlobalThreshold = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log(`Admin: Updating global threshold to ${globalThreshold}%`);
      await APIService.AdminUpdateGlobalThreshold(globalThreshold);
      setSuccessMessage(
        `La soglia globale di allerta è stata aggiornata correttamente al ${globalThreshold}%.`,
      );
      setIsSuccessDialogOpen(true);
    } catch (err: any) {
      console.error("Admin: Failed to update global threshold", err.message);
      alert("Errore durante l'aggiornamento della soglia: " + err.message);
    }
  };

  const handleAddOrUpdateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName) return;

    try {
      console.log(
        `Admin: Upserting tag ${newTagName} (Thresh: ${newTagThreshold}%, Pause: ${newTagPause}m)`,
      );
      // Update APIService signature to accept the 3rd parameter: newTagPause
      await APIService.AdminSetTagThreshold(
        newTagName,
        newTagThreshold,
        newTagPause,
      );

      setNewTagName("");
      setNewTagThreshold(10);
      setNewTagPause(10);

      const updatedTags = await APIService.AdminGetTags();
      setTags(updatedTags);
    } catch (err: any) {
      console.error("Admin: Failed to update tag", err.message);
      alert("Errore nell'aggiornamento del tag: " + err.message);
    }
  };

  const handleEditTag = (tag: TagConfig) => {
    setNewTagName(tag.tag_name);
    setNewTagThreshold(tag.threshold);
    setNewTagPause(tag.pause_minutes || 10);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const initiateTagDelete = (tag: TagConfig) => {
    setTagToDelete(tag);
    setIsTagDeleteDialogOpen(true);
  };

  const handleCloseTagDeleteDialog = () => {
    setIsTagDeleteDialogOpen(false);
    setTagToDelete(null);
  };

  const handleConfirmDeleteTag = async () => {
    if (!tagToDelete) return;

    try {
      console.log(`Admin: Deleting tag ${tagToDelete.tag_name}`);
      await APIService.AdminDeleteTag(tagToDelete.tag_name);
      await fetchAdminData();
    } catch (err: any) {
      console.error("Admin: Error deleting tag:", err.message);
      alert("Errore durante l'eliminazione del tag: " + err.message);
    } finally {
      handleCloseTagDeleteDialog();
    }
  };

  // --- Users Handlers ---
  const initiateRoleChange = (user: UserProfile) => {
    setRoleChangeUser(user);
    setIsDialogOpen(true);
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeUser) return;

    const newRole = roleChangeUser.role === "admin" ? "user" : "admin";
    console.log(
      `Admin: Confirming role change for ${roleChangeUser.email} to ${newRole}`,
    );

    try {
      await APIService.AdminUpdateUser(
        roleChangeUser.email,
        newRole,
        roleChangeUser.enabled,
      );
      await fetchAdminData();
    } catch (err: any) {
      console.error("Admin: Error updating role:", err);
      alert("Aggiornamento ruolo fallito: " + err.message);
    } finally {
      setIsDialogOpen(false);
      setRoleChangeUser(null);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setRoleChangeUser(null);
  };

  const toggleUserStatus = async (user: UserProfile) => {
    try {
      console.log(`Admin: Toggling status for ${user.email}`);
      await APIService.AdminUpdateUser(user.email, user.role, !user.enabled);
      await fetchAdminData();
    } catch (err: any) {
      alert("Aggiornamento fallito: " + err.message);
    }
  };

  const initiateUserDelete = (user: UserProfile) => {
    setUserToDelete(user);
    setIsUserDeleteDialogOpen(true);
  };

  const handleCloseUserDeleteDialog = () => {
    setIsUserDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      console.log(`Admin: Deleting user ${userToDelete.email}`);
      await APIService.AdminDeleteUser(userToDelete.email);
      await fetchAdminData();
    } catch (err: any) {
      console.error("Admin: Error deleting user:", err.message);
      alert("Eliminazione fallita: " + err.message);
    } finally {
      handleCloseUserDeleteDialog();
    }
  };

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail) return;
    try {
      console.log(`Admin: Adding new user ${newUserEmail}`);
      await APIService.AdminAddUser(newUserEmail);
      setNewUserEmail("");
      await fetchAdminData();
    } catch (err: any) {
      alert("Errore durante l'aggiunta dell'utente: " + err.message);
    }
  };

  if (loading) {
    return (
      <Box
        className="admin-loading"
        sx={{ display: "flex", justifyContent: "center", mt: 10 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const targetRole =
    roleChangeUser?.role === "admin" ? "Utente" : "Amministratore";

  return (
    <div className="admin-page-root">
      {/* Header */}
      <div className="admin-header-bar">
        <IconButton onClick={() => navigate("/dashboard")} color="inherit">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" component="h1">
          Amministrazione di Sistema
        </Typography>
        <div style={{ width: 48 }} />
      </div>

      <Container
        maxWidth="lg"
        className="admin-main-content"
        sx={{ mt: 4, mb: 4 }}
      >
        {/* --- SYSTEM SETTINGS SECTION --- */}
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
        >
          <Settings size={20} /> Impostazioni Soglie
        </Typography>

        <div className="admin-controls-liner">
          {/* Row 1: Global Notification Pause Form */}
          <div
            className="admin-controls"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1.5, fontWeight: "bold" }}
            >
              Timeout Pausa Notifiche Globale (Minuti)
            </Typography>
            <form
              onSubmit={handleUpdatePauseMinutes}
              className="add-user-form"
              style={{ width: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "12px",
                  alignItems: isMobile ? "stretch" : "center",
                }}
              >
                <input
                  type="number"
                  min="1"
                  required
                  placeholder="Minuti di pausa..."
                  value={pauseMinutes}
                  onChange={(e) =>
                    setPauseMinutes(parseInt(e.target.value, 10))
                  }
                  style={{ flex: 1 }}
                />
                <button type="submit">
                  <Save size={18} /> Salva
                </button>
              </div>
            </form>
          </div>

          {/* Row 2: Group Notification Pause Form */}
          <div
            className="admin-controls"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1.5, fontWeight: "bold" }}
            >
              Timeout Pausa Notifiche Gruppi (Minuti)
            </Typography>
            <form
              onSubmit={handleUpdateGroupPauseMinutes}
              className="add-user-form"
              style={{ width: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "12px",
                  alignItems: isMobile ? "stretch" : "center",
                }}
              >
                <input
                  type="number"
                  min="1"
                  required
                  placeholder="Minuti di pausa gruppi..."
                  value={groupPauseMinutes}
                  onChange={(e) =>
                    setGroupPauseMinutes(parseInt(e.target.value, 10))
                  }
                  style={{ flex: 1 }}
                />
                <button type="submit">
                  <Save size={18} /> Salva
                </button>
              </div>
            </form>
          </div>

          <div
            className="admin-controls"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            {/* Row 3: Global Threshold Form */}
            <Typography
              variant="subtitle2"
              sx={{ mb: 1.5, fontWeight: "bold" }}
            >
              Soglia Globale (%)
            </Typography>
            <form
              onSubmit={handleUpdateGlobalThreshold}
              className="add-user-form"
              style={{ width: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "12px",
                  alignItems: isMobile ? "stretch" : "center",
                }}
              >
                <input
                  type="number"
                  min="0"
                  max="100"
                  required
                  placeholder="Valore soglia..."
                  value={globalThreshold}
                  onChange={(e) =>
                    setGlobalThreshold(parseInt(e.target.value, 10))
                  }
                  style={{ flex: 1 }}
                />
                <button type="submit">
                  <Save size={18} /> Salva
                </button>
              </div>
            </form>
          </div>
        </div>

        <div
          className="admin-controls"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          {/* Row 4: Tag Threshold & Pause Form */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "bold" }}>
            Imposta Soglia e Ritardo per KSP (Tag)
          </Typography>
          <form
            onSubmit={handleAddOrUpdateTag}
            className="add-user-form"
            style={{ width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "12px",
              }}
            >
              <div
                style={{ flex: 2, display: "flex", flexDirection: "column" }}
              >
                <Autocomplete
                  freeSolo
                  options={availableTags}
                  value={newTagName}
                  onInputChange={(event, newInputValue) => {
                    setNewTagName(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    // Handles selection from the dropdown list
                    setNewTagName(newValue || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Nome Tag (es. backend)"
                      required
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          height: "42px", // Matches your existing button height
                        },
                      }}
                    />
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, ml: 1, color: "text.secondary" }}
                >
                  Identificativo Tag
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flex: 2,
                  width: "100%",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    placeholder="Soglia %"
                    title="Soglia di allerta (%)"
                    value={newTagThreshold}
                    onChange={(e) =>
                      setNewTagThreshold(parseInt(e.target.value, 10))
                    }
                    style={{ width: "100%" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, ml: 1, color: "text.secondary" }}
                  >
                    Soglia %
                  </Typography>
                </div>

                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <input
                    type="number"
                    min="1"
                    required
                    placeholder="Ritardo (min)"
                    title="Minuti di attesa prima di notificare"
                    value={newTagPause}
                    onChange={(e) =>
                      setNewTagPause(parseInt(e.target.value, 10))
                    }
                    style={{ width: "100%" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, ml: 1, color: "text.secondary" }}
                  >
                    Minuti ritardo
                  </Typography>
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <button type="submit" style={{ height: "42px" }}>
                    <Tag size={18} /> Applica
                  </button>
                  {/* Placeholder spaziale per allineare il bottone agli input con caption */}
                  <Box sx={{ height: "20px" }} />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Existing Tags Table */}
        {tags.length > 0 && (
          <div
            className="table-wrapper"
            style={{ marginBottom: "2rem", overflowX: "auto" }}
          >
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Tag Configurato</th>
                  <th>Soglia Allerta</th>
                  <th>Ritardo Notifica</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <tr key={tag.tag_name}>
                    <td>
                      <strong>{tag.tag_name}</strong>
                    </td>
                    <td>{tag.threshold}%</td>
                    <td>{tag.pause_minutes || 10} min</td>
                    <td style={{ display: "flex" }}>
                      <button
                        className="delete-btn"
                        style={{ color: "#1976d2", marginRight: "16px" }}
                        onClick={() => handleEditTag(tag)}
                        title="Modifica Tag"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => initiateTagDelete(tag)}
                        title="Elimina Tag"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Divider sx={{ my: 4 }} />

        {/* --- USER MANAGEMENT SECTION --- */}
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
        >
          <UserPlus size={20} /> Gestione Utenti
        </Typography>

        <div className="admin-controls">
          <form
            onSubmit={addUser}
            className="add-user-form"
            style={{ width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "8px",
                width: "100%",
              }}
            >
              <input
                type="email"
                placeholder="Email nuovo utente..."
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="submit">
                <UserPlus size={18} /> Aggiungi Utente
              </button>
            </div>
          </form>
        </div>

        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email Utente</th>
                <th>Ruolo</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.email}
                  className={!user.enabled ? "disabled-row" : ""}
                >
                  <td>{user.email}</td>
                  <td>
                    <button
                      className={`role-badge-toggle ${user.role}`}
                      onClick={() => initiateRoleChange(user)}
                      title="Clicca per cambiare ruolo"
                    >
                      {user.role === "admin" ? (
                        <ShieldCheck size={14} />
                      ) : (
                        <ShieldAlert size={14} />
                      )}
                      {user.role === "admin" ? "Admin" : "Utente"}
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleUserStatus(user)}
                      className={`status-toggle ${user.enabled ? "on" : "off"}`}
                    >
                      {user.enabled ? (
                        <CheckCircle size={18} />
                      ) : (
                        <XCircle size={18} />
                      )}
                      {user.enabled ? "Attivo" : "Disattivato"}
                    </button>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => initiateUserDelete(user)}
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>

      {/* --- MODALS --- */}
      {/* Role Change Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{"Modificare Ruolo Utente?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler cambiare il ruolo di{" "}
            <strong>{roleChangeUser?.email}</strong> in{" "}
            <strong>{targetRole}</strong>?
            <br />
            <br />
            {targetRole === "Amministratore"
              ? "Gli amministratori hanno accesso completo alle impostazioni di sistema e alla gestione utenti."
              : "Gli utenti hanno accesso limitato esclusivamente alla dashboard."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Annulla
          </Button>
          <Button onClick={handleConfirmRoleChange} color="error" autoFocus>
            Conferma Modifica
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Delete Dialog */}
      <Dialog
        open={isUserDeleteDialogOpen}
        onClose={handleCloseUserDeleteDialog}
      >
        <DialogTitle>{"Eliminare Utente?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare definitivamente l'utente{" "}
            <strong>{userToDelete?.email}</strong>?
            <br />
            <br />
            Questa azione è irreversibile e l'utente non potrà più accedere al
            sistema.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDeleteDialog} color="primary">
            Annulla
          </Button>
          <Button onClick={handleConfirmDeleteUser} color="error" autoFocus>
            Elimina Utente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Delete Dialog */}
      <Dialog open={isTagDeleteDialogOpen} onClose={handleCloseTagDeleteDialog}>
        <DialogTitle>{"Eliminare Configurazione Tag?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la configurazione per il tag{" "}
            <strong>{tagToDelete?.tag_name}</strong>?
            <br />
            <br />
            Questa azione rimuoverà ogni soglia di allerta personalizzata
            associata a questo tag.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagDeleteDialog} color="primary">
            Annulla
          </Button>
          <Button onClick={handleConfirmDeleteTag} color="error" autoFocus>
            Elimina Tag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
      >
        <DialogTitle>{"Impostazioni Salvate"}</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsSuccessDialogOpen(false)}
            color="primary"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminPage;
